<template>
  <div class="hostmap-topology" v-loading="d3SimulationLoading === 'loading'">
    <div class="network-toolbar">
      <div class="agent-legend">
        <el-dropdown trigger="click" @command="applyGrouping">
          <el-button type="text" class="cursor-pointer">
            <span class="el-dropdown-link">
              {{ topologyFilter }} <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="None">None</el-dropdown-item>
            <el-dropdown-item command="Criticality">Criticality</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>
    <div><svg class="hostmap-topology-svg" /></div>
    <div class="network-timeline">
      <el-slider
        v-model="dateRangeInput.value"
        :show-tooltip="false"
        :marks="dateRangeInput.marks()"
        :step="dateRangeInput.step"
        @change="updateChartData"
        range
      ></el-slider>
    </div>
  </div>
</template>

<script>
import { Loading } from "element-ui";
import { chartGlobalLabelDefaults } from "@/constants/chart-global-styles.constants";
import { RiskLevelEnum } from "@/constants/risk-level.constants";
import { HostStatusEnum } from "@/constants/host-status.constants";
import { HOSTMAP_NUM_OF_DAYS } from "@/constants/statistics.constants";
import { DATETIME_STATS_DISPLAY_FORMAT, DATETIME_STATS_ARGS_FORMAT } from "@/constants/datetime.constants";

export default {
  name: "D3HostmapTopology",
  props: {
    chartId: { type: String },
    chartOptions: {
      type: Object,
      default: () => {
        return {};
      }
    },
    chartData: { type: Object },
    chartPlugins: { type: Object }
  },
  data() {
    return {
      topologyFilter: "Group By",
      d3SimulationLoading: "loading",
      graphOptions: {
        size: { w: 400, h: 400 },
        style: chartGlobalLabelDefaults.style
      },
      dateRangeInput: {
        value: [this.chartData.rangeStart, this.chartData.rangeEnd],
        marks: () => {
          return this.chartData.getDateRangeSteps();
        },
        step: 100 / (HOSTMAP_NUM_OF_DAYS - 1),
        formatTooltip(val) {
          console.log(val);
        }
      },
      simulation: {},
      d3Slider: {
        svg: {}
      },
      d3Links: {
        group: {}
      },
      d3ColHeaders: {
        group: {}
      },
      debounceResize: {}
    };
  },
  computed: {
    d3Zoom() {
      let vm = this;
      return {
        svg: {},
        handler: {},
        value: 1, // initially at 100% zoom
        step: 0.1, // 10% zoom step
        min: 0.1,
        max: 8,
        formatVal(val) {
          return `${Math.round(val * 100)}%`;
        },
        changeZoom(val) {
          vm.d3Zoom.handler.scaleTo(vm.d3Zoom.svg, this.value);
        }
      };
    },
    d3Nodes() {
      let vm = this;
      return {
        group: {},
        click(d) {
          let elem = this;
          if (!d) {
            return false;
          }
          vm.clearNodeClasses(["active"]);
          elem.classList.add("active");
          vm.addLinkGroupClass(d.name, ["active"]);

          vm.$emit("node-click", d);
        }
      };
    }
  },
  methods: {
    clearNodeClasses(classList) {
      let nodeGroups = this.d3Nodes.group._groups;
      if (this.$_.isArray(nodeGroups[0])) {
        nodeGroups[0].forEach(g => {
          g.classList.remove(...classList);
        });
      }

      let linkGroups = this.d3Links.group._groups;
      if (this.$_.isArray(linkGroups[0])) {
        linkGroups[0].forEach(line => {
          line.classList.remove(...classList);
          line.attributes["marker-end"].value = "url(#end)";
        });
      }
    },
    addLinkGroupClass(agentName, classList) {
      let linkGroups = this.d3Links.group._groups;
      if (this.$_.isArray(linkGroups[0])) {
        linkGroups[0].forEach(line => {
          if (
            _.indexOf(line.classList, `to_${agentName}`) > -1 ||
            _.indexOf(line.classList, `from_${agentName}`) > -1
          ) {
            line.classList.add(...classList);
            line.attributes["marker-end"].value = "url(#active_end)";
          }
        });
      }
    },
    updateChartData(dateRange) {
      this.d3SimulationLoading = "loading";

      let rangeStart = this.chartData.dateRange[dateRange[0]];
      let rangeEnd = this.chartData.dateRange[dateRange[1]];

      this.chartData.setDateRange(dateRange[0], dateRange[1]);

      let starttime = this.$moment.utc();
      let endtime = this.$moment.utc();

      if (rangeStart !== this.nowString) {
        starttime = starttime.diff(this.$moment(rangeStart.value, DATETIME_STATS_ARGS_FORMAT), "days");
        starttime = this.$moment().subtract(starttime, "days");
      }

      if (rangeEnd !== this.nowString) {
        endtime = endtime.diff(this.$moment(rangeEnd.value, DATETIME_STATS_ARGS_FORMAT), "days");
        endtime = this.$moment().subtract(starttime, "days");
      }

      this.$nextTick(() => {
        this.$emit("update-chartdata", {
          starttime: starttime.toISOString(), // format puts in ISO 8601
          endtime: endtime.toISOString() // format puts in ISO 8601
        });
      });
    },
    applyGrouping(sel) {
      this.d3SimulationLoading = "loading";
      let group = sel.toLowerCase();
      this.chartData.setLinks(this.chartData.links);
      this.$nextTick(() => {
        switch (group) {
          case "criticality":
            this.topologyFilter = sel;
            this.chartData.applyGroupBy(group);
            this.animate(this.chartData);
            break;
          default:
            this.topologyFilter = "Group By";
            this.chartData.applyGroupBy("");
            this.animate(this.chartData);
        }
      });
    },
    simulate(nodes, links, groups) {
      let width = this.graphOptions.size.w;
      let height = this.graphOptions.size.h;
      let centerForce = { width: width * 0.8, height: height * 0.7 };
      let simulation = this.$d3
        .forceSimulation()
        .force(
          "link",
          this.$d3
            .forceLink()
            .id(function(d) {
              return d.id;
            })
            .distance(100)
            .strength(0.001)
        )
        .force(
          "charge",
          this.$d3
            .forceManyBody()
            .strength(-200)
            .distanceMax(250)
            .distanceMin(50)
        )
        .force(
          "x",
          this.$d3
            .forceX(function(d) {
              if (d.group === "1") {
                return (1 * centerForce.width) / 4;
              } else if (d.group === "2") {
                return (2 * centerForce.width) / 4;
              } else if (d.group === "3") {
                return (3 * centerForce.width) / 4;
              } else {
                return (0 * centerForce.width) / 4;
              }
            })
            .strength(1)
        )
        .force("y", this.$d3.forceY(height / 2))
        .force("center", this.$d3.forceCenter(centerForce.width / 2, centerForce.height / 2))
        .force("collision", this.$d3.forceCollide().radius(25));

      return simulation;
    },
    buildNodes(graph) {
      let vm = this;
      this.d3Nodes.group = this.d3Zoom.svg
        .append("g")
        .attr("class", "nodes")
        .selectAll("g")
        .data(graph.nodes)
        .enter()
        .append("g")
        .attr("class", function(d) {
          return `group${d.group} host_${d.agentId}`;
        })
        .on("click", this.d3Nodes.click);

      let rect = this.d3Nodes.group
        .append("rect")
        .attr("class", "nodes-rect")
        .attr("width", 100)
        .attr("height", 20)
        .attr("x", -10)
        .attr("y", -10)
        .attr("rx", 10) // radius of corners
        .attr("ry", 10) // radius of corners
        .attr("stroke", function(d) {
          return `${vm.graphOptions.style.borderColor}`;
        })
        .attr("stroke-width", function(d) {
          return "1";
        })
        .attr("fill", function(d) {
          return `${vm.graphOptions.style.circleFillColor}`;
        });

      let circles = this.d3Nodes.group
        .append("circle")
        .attr("r", 5)
        .attr("fill", function(d) {
          if (d.state === HostStatusEnum.properties.offline.state) {
            return HostStatusEnum.properties.offline.color;
          } else if (d.state === HostStatusEnum.properties.safe.state) {
            return HostStatusEnum.properties.safe.color;
          } else if (d.state === HostStatusEnum.properties.at_risk.state) {
            // Color according to host risk level if host aggregatedRiskScore > 0
            if (d.riskLevel.level === RiskLevelEnum.HIGH) {
              return RiskLevelEnum.properties.high.color;
            } else if (d.riskLevel.level === RiskLevelEnum.MEDIUM) {
              return RiskLevelEnum.properties.medium.color;
            } else {
              return RiskLevelEnum.properties.low.color;
            }
          } else {
            return HostStatusEnum.properties.safe.color;
          }
        });

      let labels = this.d3Nodes.group
        .append("text")
        .text(function(d) {
          if (d.id.length < 10) {
            return d.id;
          }
          return d.id.slice(0, 9).concat("...");
        })
        .attr("x", 6)
        .attr("y", 3);

      // tooltip
      this.d3Nodes.group.append("title").text(function(d) {
        return d.id;
      });

      this.d3Nodes.group.exit().remove();
    },
    buildLinks(graph) {
      let vm = this;
      this.d3Links.group = this.d3Zoom.svg
        .append("g")
        .attr("class", "links")
        .selectAll("line")
        .data(graph.links)
        .enter()
        .append("line")
        .attr("class", function(d) {
          if (!d) {
            return false;
          }
          return `to_${d.target} from_${d.source}`;
        })
        .attr("stroke", function(d) {
          return `${vm.graphOptions.style.dashlineColor}`;
        })
        .attr("stroke-dasharray", function(d) {
          return "2";
        })
        .attr("stroke-width", function(d) {
          return "1";
        })
        .attr("marker-end", "url(#end)");

      this.d3Links.group.exit().remove();
    },
    buildColHeaders(graph) {
      let vm = this;
      let width = this.graphOptions.size.w * 0.9;
      this.d3ColHeaders.group = this.d3Zoom.svg.append("g").attr("class", "header");

      if (!this.$_.isEmpty(graph.groupByProperty)) {
        let columnWidth = Math.floor(width / this.chartData.groupBy.length);

        // create border arounder column headers
        // top line
        this.d3ColHeaders.group
          .append("rect")
          .attr("width", "100%")
          .attr("height", 1)
          .attr("stroke", function(d) {
            return `${vm.graphOptions.style.borderColor}`;
          })
          .attr("stroke-width", function(d) {
            return "1";
          })
          .attr("fill", function(d) {
            return `white`;
          });

        // bottom line
        this.d3ColHeaders.group
          .append("rect")
          .attr("width", "100%")
          .attr("height", 1)
          .attr("y", 30)
          .attr("stroke", function(d) {
            return `${vm.graphOptions.style.borderColor}`;
          })
          .attr("stroke-width", function(d) {
            return "1";
          })
          .attr("fill", function(d) {
            return `white`;
          });

        graph.groupBy.forEach((group, idx) => {
          let startX = 50; // 50px from top left
          if (idx > 0) {
            startX += idx * columnWidth;
          }
          this.d3ColHeaders.group
            .append("text")
            .text(d => {
              return group;
            })
            .attr("x", startX)
            .attr("y", 20);
        });
      }
    },
    buildLinkArrows(graph) {
      let vm = this;
      let arrowhead = this.d3Zoom.svg
        .append("defs")
        .selectAll("marker")
        .data(["end"])
        .enter();

      arrowhead
        .append("marker")
        .attr("id", "end")
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", 22)
        .attr("refY", 0)
        .attr("markerWidth", 10)
        .attr("markerHeight", 10)
        .attr("orient", "auto")
        .attr("xoverflow", "visible")
        .append("path")
        .attr("d", "M 0,-5 L 10 ,0 L 0,5")
        .attr("fill", `${this.graphOptions.style.dashlineColor}`)
        .style("stroke", `${this.graphOptions.style.dashlineColor}`)
        .attr("stroke-width", "2");

      arrowhead
        .append("marker")
        .attr("id", "active_end")
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", 22)
        .attr("refY", 0)
        .attr("markerWidth", 10)
        .attr("markerHeight", 10)
        .attr("orient", "auto")
        .attr("xoverflow", "visible")
        .append("path")
        .attr("d", "M 0,-5 L 10 ,0 L 0,5")
        .attr("fill", `${this.graphOptions.style.darkDashlineColor}`)
        .style("stroke", `${this.graphOptions.style.darkDashlineColor}`)
        .attr("stroke-width", "2");
    },
    animate(graph) {
      this.d3SimulationLoading = "loading";

      let groups = this.d3Zoom.svg.selectAll("g");
      groups.selectAll("*").remove();

      this.buildColHeaders(graph);
      this.buildLinks(graph);
      this.buildNodes(graph);
      this.startSimulation(graph);
    },
    startSimulation(graph) {
      let vm = this;
      if (!_.isEmpty(this.simulation)) {
        this.simulation.stop();
      }
      this.simulation = this.simulate();
      this.simulation.nodes(graph.nodes).on("tick", ticked);
      this.simulation.force("link").links(graph.links);
      this.simulation.restart();

      function ticked() {
        vm.d3Links.group
          .attr("x1", function(d) {
            return d.source.x;
          })
          .attr("y1", function(d) {
            return d.source.y;
          })
          .attr("x2", function(d) {
            if (d.source.group > d.target.group) {
              return d.target.x + 80; // offset for points going backwards
            }
            return d.target.x;
          })
          .attr("y2", function(d) {
            return d.target.y;
          });

        vm.d3Nodes.group.attr("transform", function(d) {
          return "translate(" + d.x + "," + d.y + ")";
        });
      }
    },
    renderChart(graph) {
      const vm = this;
      let svg = this.$d3
        .select(".hostmap-topology-svg")
        .attr("height", this.graphOptions.size.h)
        .attr("width", this.graphOptions.size.w)
        .attr(
          "style",
          `font: ${this.graphOptions.style.smallText} ${this.graphOptions.style.fontFamily}; fill: ${
            this.graphOptions.style.textfillColor
          }`
        )
        .attr("cursor", "grab");

      let overlayRect = svg
        .append("rect")
        .attr("class", "overlay")
        .attr("width", this.graphOptions.size.w)
        .attr("height", this.graphOptions.size.h)
        .style("fill", "none")
        .style("pointer-events", "all");

      this.d3Zoom.svg = svg
        .append("g")
        .attr("class", "g-zoom")
        .attr("transform", `scale(${this.d3Zoom.value})`);

      // apply zoom on main svg:g
      this.d3Zoom.handler = this.$d3
        .zoom()
        .scaleExtent([this.d3Zoom.min, this.d3Zoom.max]) // zoom limit
        .on("zoom", function(d) {
          let eventTransform = vm.$d3.event.transform;
          vm.d3Zoom.svg.attr("transform", eventTransform);
          if (!_.isEmpty(vm.d3Slider.svg)) {
            vm.d3Zoom.value = eventTransform.k;
            vm.d3Slider.svg.property("value", eventTransform.k);
          }
        });

      svg.call(this.d3Zoom.handler);

      this.d3Slider.svg = this.$d3
        .select(".network-toolbar")
        .append("div")
        .attr("class", "toolbar-slider")
        .append("input")
        .datum({})
        .attr("type", "range")
        .attr("value", this.d3Zoom.value)
        .attr("min", this.d3Zoom.min)
        .attr("max", this.d3Zoom.max)
        .attr("step", (this.d3Zoom.handler.scaleExtent()[1] - this.d3Zoom.handler.scaleExtent()[0]) / 100)
        .on("input", function(d) {
          vm.d3Zoom.handler.scaleTo(vm.d3Zoom.svg, this.value);
        });

      this.buildLinkArrows(graph);
      this.animate(graph);
    },
    setDimensions(animate = true) {
      let offset = 225;

      this.graphOptions.size.w = `${this.$el.parentElement.clientWidth}`;
      this.graphOptions.size.h = `${this.$el.parentElement.clientHeight - offset}`; // need to provide offset for toolbar and footer
      if (animate) {
        this.animate(this.chartData);
      }
    }
  },
  mounted() {
    this.setDimensions(false);
    this.renderChart(this.chartData);

    this.debounceResize = _.debounce(this.setDimensions, 500);
    // window resize event
    window.addEventListener("resize", this.debounceResize);
  },
  updated: function() {
    // End of d3 simulation, fix position of each node respective columns by group
    this.$nextTick(() => {
      setTimeout(() => {
        let width = this.graphOptions.size.w * 0.9;
        let start = {
          x: 50,
          y: 75
        };
        let padding = {
          x: Math.floor(width / this.chartData.groupBy.length),
          y: 50
        };
        let groupPositions = {
          group1: {
            x: start.x,
            y: start.y
          },
          group2: {
            x: start.x + padding.x,
            y: start.y
          },
          group3: {
            x: start.x + 2 * padding.x,
            y: start.y
          }
        };
        this.chartData.nodes.forEach(node => {
          switch (node.group) {
            case "1":
              node.fx = groupPositions.group1.x;
              node.fy = groupPositions.group1.y;
              groupPositions.group1.y += padding.y;
              break;
            case "2":
              node.fx = groupPositions.group2.x;
              node.fy = groupPositions.group2.y;
              groupPositions.group2.y += padding.y;
              break;
            default:
              node.fx = groupPositions.group3.x;
              node.fy = groupPositions.group3.y;
              groupPositions.group3.y += padding.y;
          }
        });

        this.d3SimulationLoading = "finished";
      }, 2000);
    });
  },
  beforeDestroy() {
    // remove event listeners when exiting view
    let svg = this.$d3.select(".hostmap-topology-svg");
    svg.selectAll("*").remove();

    window.removeEventListener("resize", this.debounceResize);
  }
};
</script>

<style>
@import "assets/styles/_variables.css";

.hostmap-topology {
  .toolbar-slider {
    input {
      appearance: none;
      margin-left: 35px;
      display: inline-block;
      width: 25%;
      height: 5px;
      background: #e4e7ed;
      outline: none;
      opacity: 0.7;
      transition: opacity 0.2s;

      &::-webkit-slider-thumb {
        appearance: none;
        width: 16px;
        height: 16px;
        border: 2px solid #409eff;
        background-color: #fff;
        border-radius: 50%;
        transition: 0.2s;
        user-select: none;
      }

      &::-moz-range-thumb {
        width: 16px;
        height: 16px;
        border: 2px solid #409eff;
        background-color: #fff;
        border-radius: 50%;
        transition: 0.2s;
        user-select: none;
      }
    }
  }

  .agent-legend {
    display: inline-block;
    float: right;
  }
  .network-timeline {
    .el-slider {
      width: 90%;
      margin-left: 35px;
      display: inline-block;
    }
  }
}

.nodes {
  .active {
    rect {
      stroke-width: 1;
      stroke: var(--aluminum-800);
      fill: var(--denim-100);
    }
  }
}

.links {
  line.active {
    stroke-width: 1;
    stroke-dasharray: 0;
    stroke: var(--aluminum-700);
  }
}
</style>

<style scoped>
@import "assets/styles/_variables.css";

>>> .el-loading-mask {
  background-color: rgba(255, 255, 255, 1);
}

>>> .el-slider__runway {
  height: 2px;
  background-color: var(--border-color);
}

>>> .el-slider__stop {
  height: 12px;
  width: 2px;
  background-color: var(--border-color);
  transform: translateY(-50%);
}
</style>



// WEBPACK FOOTER //
// src/components/network-graph/D3HostmapTopology.vue